import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  inject,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { dateRangeValidator } from '@core/validators/date-range.validator';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import {
  BolseiroService,
  BolsaService,
  InstituicaoEnsinoService,
  EnumService,
} from '../../../api/services';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { Pagination } from '@app/shared/model/pagination.model';
import { takeUntil, Subject, forkJoin, Observable, tap } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ApiService } from '@core/providers/http';
import { toExtensoComMoeda } from '@app/core/helpers/toExtensoComMoeda';
interface VerificarRegraPagamentoPayload {
  id: string;
  pedidoID: number;
  bolseiroID: number;
  requerenteID: number;
  isActive: boolean;
  tipoBolsa: number;
  paisID: number;
  instituicaoGestoraID: number;
  nivelAcademico: number;
  areaProgramaFormacaoID: number;
  programaFormacaoID: number;
  anoCurricularFrequencia: string;
  valorSubsidio: number;
}

@Component({
  selector: 'app-comprovativo-pagamento-de-subsidios',
  standalone: true,
  imports: [
    DatePicker,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    CommonModule,
    SelectModule,
    TableModule,
    DataVoidComponent,
    ConfirmDialogModule,
  ],
  providers: [
    BolseiroService,
    BolsaService,
    InstituicaoEnsinoService,
    ConfirmationService,
    ApiService,
    EnumService,
  ],
  templateUrl: './comprovativo-pagamento-de-subsidios.component.html',
  styleUrl: './comprovativo-pagamento-de-subsidios.component.scss',
})
export class ComprovativoPagamentoDeSubsidiosComponent
  implements OnInit, OnChanges, OnDestroy {
  @Output() formValueChanges = new EventEmitter<{
    data: any;
    valid: boolean;
  }>();
  @Input() bolseiroID!: string;
  @Input() documento: any;
  @Input() disabled = false;
  private currencyToValueMap: { [key: string]: string } = {
    KZ: 'KWANZAS',
    EUR: 'EUROS',
    USD: 'DÓLARES',
  };
  private destroy$ = new Subject<void>();

  confirm = inject(ConfirmationService);
  bolsaService = inject(BolsaService);
  bolseiroService = inject(BolseiroService);
  instituicaoEnsinoService = inject(InstituicaoEnsinoService);
  enumService = inject(EnumService);
  fb = inject(FormBuilder);

  form!: FormGroup;
  pagination: Pagination<any> = new Pagination<any>();
  Estudantes: any[] = [];
  students: any[] = [];
  InstEnsino: any[] = [];
  nivelAcademicoOptions: { id: number; label: string }[] = [];

  previousInstituicaoId: string | null = null;
  bolsa: any;
  valorRegra: any = null;

  periods = [
    // { label: 'Mês', time: 'Mês', recurrency: 'month', number: 1 },
    { label: 'Trimestral', time: 'Trimestre', recurrency: 'month', number: 3 },
    // { label: 'Semestre', time: 'Semestre', recurrency: 'month', number: 6 },
    // { label: 'Ano', time: 'Ano', recurrency: 'year', number: 1 },
  ];

  // -------- lifecycle --------
  ngOnInit(): void {
    this.buildForm();
    this.previousInstituicaoId = this.form.get('instituicao')?.value ?? null;
    this.wireCoreChanges();

    this.loadEnums().subscribe();
    this.getAllInstituicoesEnsino();
  }

  ngOnChanges(): void {
    this.syncDisabled();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -------- form & wiring --------
  private buildForm(): void {
    this.form = this.fb.group(
      {
        estudanteID: [null, Validators.required],
        bolsaID: [''],
        valores: [{ value: '', disabled: true }],
        nivelAcademico: [''],
        periodicidade: [{ value: 'Trimestre', disabled: true }, Validators.required],
        instituicao: ['', Validators.required],
        datainicio: ['', Validators.required],
        datafim: ['', Validators.required],
        moeda: [{ value: '', disabled: false }],
        dataEmissao: [this.currentDate, Validators.required],
        idioma: ['pt', Validators.required],
      },
      {
        validators: dateRangeValidator(
          'datainicio',
          'datafim',
          'dataInicioFimRange'
        ),
      }
    );
  }

  private wireCoreChanges(): void {
    // Emite payload sempre que o form muda
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitDocumentData());
    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitDocumentData());

    // Mudança de instituição → confirmação + reset lista estudantes
    this.form
      .get('instituicao')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((novoId: string) => this.handleInstituicaoChange(novoId));

    // Mudança de estudante → reset e recarregar bolsa/valor da regra
    this.form
      .get('estudanteID')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetBolsaCampos(); // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< aplica reset aqui
        if (!value) return;
        this.loadAllBolsa({ BolseiroID: value });
      });
  }

  private syncDisabled(): void {
    if (!this.form) return;
    if (this.disabled) this.form.disable({ emitEvent: false });
    else this.form.enable({ emitEvent: false });
  }

  get currentDate() {
    return new Date();
  }

  get f() {
    return this.form.controls;
  }

  // -------- data loading --------
  loadEnums(): Observable<any> {
    return forkJoin({
      nivelAcademico: this.enumService.getAllNivelAcademicoMIREMPETEnum(),
    }).pipe(
      tap((response) => {
        const map = response?.nivelAcademico?.data ?? {};
        this.nivelAcademicoOptions = Object.entries(map).map(
          ([key, value]: any) => ({
            id: Number(key),
            label: String(value),
          })
        );
      })
    );
  }

  getAllInstituicoesEnsino(): void {
    this.instituicaoEnsinoService
      .list({ pageSize: 100, PaisID: '193' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (institutions) => (this.InstEnsino = institutions.data),
        error: (e) => console.error('Erro ao carregar instituições:', e),
      });
  }

  getAllBolseiros(params: any = {}): void {
    this.bolseiroService
      .list({ ...params, pageSize: 100, OrderBy: 'id desc' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (bolseiros) => (this.Estudantes = bolseiros.data || []),
        error: (e) => console.error('Erro ao carregar bolseiros:', e),
      });
  }

  loadAllBolsa(queryParams: any = {}): void {
    const params = {
      ...queryParams,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 5,
    };

    this.bolsaService
      .list({
        ...params,
        BolseiroID: queryParams.BolseiroID || this.bolseiroID,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.pagination = new Pagination<any>().deserialize(response);
          this.bolsa = response?.data?.[0];
          this.form
            .get('bolsaID')
            ?.setValue(this.bolsa?.id || null, { emitEvent: false });
          this.fetchValorByRegra(this.bolsa);
        },
        error: (e) => console.error('Erro ao carregar bolsa:', e),
      });
  }

  // -------- regras/valor --------
  private pickMatricula(b: any) {
    const arr = Array.isArray(b?.matricula) ? b.matricula : [];
    if (!arr.length) return null;

    const emCurso = arr.find(
      (m: any) => (m?.estadoDescricao || '').toLowerCase() === 'em curso'
    );
    if (emCurso) return emCurso;

    const parse = (d: any) => (d ? new Date(d).getTime() : 0);
    return (
      arr
        .slice()
        .sort(
          (a: any, c: any) => parse(c?.dataInicio) - parse(a?.dataInicio)
        )[0] || null
    );
  }

  private buildRegraPayload(bolsa: any): VerificarRegraPagamentoPayload {
    const matricula = this.pickMatricula?.(bolsa);
    const anoCurricular = matricula?.anoCurricular ?? bolsa?.anoCurricular ?? 1;

    const programaFormacaoID =
      bolsa?.programaFormacaoID ??
      bolsa?.areaProgramaFormacao?.programaFormacaoId ??
      bolsa?.areaProgramaFormacao?.programaFormacaoID ??
      0;

    const bolseiroID = Number(bolsa?.bolseiroID ?? bolsa?.bolseiro?.id ?? 0);

    return {
      id: '0',
      pedidoID: Number(bolsa?.pedidoID ?? 0),
      bolseiroID,
      requerenteID: Number(bolsa?.requerenteID ?? 0),
      isActive: Boolean(bolsa?.isActive ?? true),
      tipoBolsa: Number(bolsa?.tipoBolsa ?? 0),
      paisID: Number(bolsa?.paisID ?? 0),
      instituicaoGestoraID: Number(
        bolsa?.instituicaoGestoraID ?? bolsa?.instituicaoGestora?.id ?? 0
      ),
      nivelAcademico: Number(bolsa?.nivelAcademico ?? 0),
      areaProgramaFormacaoID: Number(
        bolsa?.areaProgramaFormacaoID ?? bolsa?.areaProgramaFormacao?.id ?? 0
      ),
      programaFormacaoID: Number(programaFormacaoID),
      anoCurricularFrequencia: String(anoCurricular),
      valorSubsidio: Number(bolsa?.valorSubsidio ?? 0),
    };
  }

  private fetchValorByRegra(bolsa: any): void {
    if (!bolsa) {
      this.valorRegra = null;
      this.emitDocumentData();
      return;
    }

    const body = this.buildRegraPayload(bolsa);
    this.bolsaService
      .verificarRegraPagamento(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          let valor = Number(res?.valorPagamento ?? 0);
          let moeda = this.form.get('moeda')?.value ?? '';

          if (!valor || isNaN(valor)) {
            const lista = Array.isArray(res?.regraPagamentoSubsidio)
              ? res.regraPagamentoSubsidio
              : [];
            const apoioFormacao =
              lista.find((x: any) => x?.subsidioId === 2) ??
              lista.find((x: any) =>
                String(x?.subsidios?.descricao ?? '')
                  .toLowerCase()
                  .includes('apoio a formação')
              );

            valor = Number(apoioFormacao?.valor ?? 0);
            moeda = apoioFormacao?.moeda ?? '';
            this.form.get('moeda')?.setValue(moeda, { emitEvent: false });
          }

          this.valorRegra =
            !isNaN(valor) && valor > 0 ? this.formatPtBR(valor) : null;
          // não preenche ‘valores’ aqui para não confundir: usamos valorRegra para a tabela e output
          this.emitDocumentData();
        },
        error: () => {
          this.valorRegra = null;
          this.form.patchValue(
            {
              valores: '0.00',
              valoresextenso: 'zero',
              moeda: '-',
            },
            { emitEvent: false }
          );
          this.emitDocumentData();
        },
      });
  }

  private formatPtBR(n: number): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n || 0);
  }

  // -------- UI handlers --------
  onPageChange(event: any): void {
    this.pagination.pageNumber = (event?.page ?? 0) + 1;
    this.pagination.pageSize = event?.rows ?? 5;
    this.loadAllBolsa({ BolseiroID: this.form.get('estudanteID')?.value });
  }

  get valorRegraNumber(): number {
    if (!this.valorRegra) return 0;
    const parsed = Number(
      String(this.valorRegra).replace(/\./g, '').replace(',', '.')
    );
    return isNaN(parsed) ? 0 : parsed;
  }

  addStudent(): void {
    const estudanteID = this.form.get('estudanteID')?.value;
    const estudante = this.Estudantes.find((e) => e.id === estudanteID);
    if (!estudante || !this.bolsa) {
      console.error('Estudante ou bolsa não encontrados!');
      return;
    }

    if (this.students.some((s) => s.id === estudanteID)) return;

    const matricula = this.pickMatricula(this.bolsa);
    const curso = matricula?.curso || '—';
    const anoCurricular = matricula?.anoCurricular || '0';

    const period = this.periods.find(
      (p) => p.time === this.form.get('periodicidade')?.value
    );
    const multiplier = period?.number || 1;
    const value = Number(this.valorRegraNumber || 0) * multiplier;

    this.form
      .get('valores')
      ?.setValue(this.formatPtBR(value || 0), { emitEvent: false });

    this.students.push({
      id: estudanteID,
      numero: this.students.length + 1,
      nome: estudante.nome,
      curso,
      anoFrequencia: anoCurricular,
      instituicao: this.bolsa?.instituicaoGestora?.nome,
      valor: this.formatPtBR(value || 0),
      moeda: this.form.get('moeda')?.value || '',
    });

    this.emitDocumentData();
  }

  removeStudent(index: number): void {
    this.students.splice(index, 1);
    this.emitDocumentData();
  }

  // -------- helpers --------
  private handleInstituicaoChange(novoId: string): void {
    const anterior = this.previousInstituicaoId;
    this.Estudantes = [];

    if (novoId) this.getAllBolseiros({ InstituicaoEnsinoID: novoId });

    if (!this.students.length || !anterior || anterior === novoId) {
      this.previousInstituicaoId = novoId ?? null;
      return;
    }

    // confirmação quando já há estudantes adicionados
    this.confirm.confirm({
      header: 'Confirmar alteração',
      message:
        'Tem a certeza que pretende mudar de instituição? Os estudantes seleccionados serão desmarcados.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, mudar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.clearStudents();
        this.previousInstituicaoId = novoId ?? null;
      },
      reject: () => {
        this.form.get('instituicao')!.setValue(anterior, { emitEvent: false });
      },
    });
  }

  private clearStudents(): void {
    this.students = [];
    this.form.get('estudanteID')!.setValue(null, { emitEvent: false });
    this.pagination = new Pagination<any>().deserialize([]);
    this.bolsa = null;
    this.valorRegra = null;
    this.emitDocumentData();
  }

  private resetBolsaCampos(): void {
    // usado quando o estudante muda
    this.bolsa = null;
    this.valorRegra = null;
    this.form.patchValue(
      {
        bolsaID: '',
        valores: '',
        moeda: '',
      },
      { emitEvent: false }
    );
    this.pagination = new Pagination<any>().deserialize([]);
  }

  private emitDocumentData(): void {
    const estudantesData = this.students.map((s, i) => ({
      numero: i + 1,
      nome: s.nome,
      curso: s.curso,
      anoFrequencia: s.anoFrequencia,
      instituicao: s.instituicao,
      valor: s.valor,
    }));

    const valorPrimeiroEstudante = estudantesData[0]?.valor || 0;
    const data = {
      ...this.form.value,
      bolsaID: this.form.get('bolsaID')?.value || 0,
      numeroReferencia: this.form.get('numeroReferencia')?.value || 'string',
      assunto: this.form.get('assunto')?.value || 'string',
      periodicidade: this.form.get('periodicidade')?.value || '',
      datainicio: this.form.get('datainicio')?.value || '',
      datafim: this.form.get('datafim')?.value || '',
      estudantes: estudantesData,
      valores: valorPrimeiroEstudante,
      valoresextenso: toExtensoComMoeda(
        valorPrimeiroEstudante,
        this.form.get('moeda')?.value || ''
      ),
      dataEmissao: this.form.get('dataEmissao')?.value || this.currentDate,
      idioma: this.form.get('idioma')?.value || 'pt',
      tipoDocumento: 6,
    };

    this.formValueChanges.emit({ data, valid: this.form.valid });
  }
}